// src/components/AppSidebar.vue

<template>
    <nav
        id="sidebar"
        v-show="sidebarStore.isSidebarOpen"
        class="collapse collapse-horizontal sticky-top show"
    >
        <div class="wrapper">
            <div class="sidebar-header">
                <h3>
                    <RouterLink to="/" class="nav-link">
                        <img
                            src="@/assets/logo-white-transparent.svg"
                            width="200"
                            alt="Cirrus Invicta"
                        />
                    </RouterLink>
                </h3>
            </div>

            <ul class="list-unstyled components">
                <CollapsibleListItem
                    v-for="menu in visibleMenus"
                    :key="menu.key || menu.title"
                    :title="menu.title"
                    :icon="menu.icon"
                    :open="menu.open"
                    :add-divider="menu.divider || false"
                >
                    <li v-for="link in menu.links" :key="link.key || link.name">
                        <RouterLink :to="link.routerLink" class="nav-link">
                            {{ link.name }}
                        </RouterLink>
                    </li>
                </CollapsibleListItem>
            </ul>
        </div>
    </nav>
</template>

<script setup>
/**
 * Sidebar with i18n-ready titles/links and DRY role filtering.
 * - Uses explicit keys from config: menu.key / link.key for translations
 * - Falls back to original English labels when no key is provided
 * - Uses vue-i18n v9 default message overload for robust translation handling
 * - Shows only menus with at least one visible link
 *
 * @typedef {{ key?: string, title: string, icon: string, open?: boolean, divider?: boolean, links: SidebarLink[] }} SidebarMenu
 * @typedef {{ key?: string, name: string, routerLink: string, roles: string[] }} SidebarLink
 */

import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CollapsibleListItem from '@/components/CollapsibleListItem.vue'
import { useMainStore } from '@/stores/main'
import { useSidebarStore } from '@/stores/sidebar'
import sidebarConfig from '@/config/sidebarConfig.json'

const { t, te } = useI18n()
const mainStore = useMainStore()
const sidebarStore = useSidebarStore()

/** @type {import('vue').Ref<{ menus: SidebarMenu[] }>} */
const config = ref(sidebarConfig)

/**
 * Single, DRY role predicate.
 * @param {string[]} required
 * @returns {boolean}
 */
const hasAnyRole = (required = []) => required.includes('all') || mainStore.hasRole(required)

/**
 * Check if a link should be visible based on roles AND organization type.
 * For Customer Management specifically, only MSP tenant admins can see it (not SME tenant admins).
 * @param {SidebarLink} link
 * @returns {boolean}
 */
const isLinkVisible = (link) => {
    const hasRole = hasAnyRole(link.roles || [])
    if (!hasRole) return false

    // Platform staff (admin/engineer) bypass organization type restrictions
    const isPlatformStaff = mainStore.hasRole(['platform_admin', 'platform_engineer'])
    if (isPlatformStaff) return true

    // Special case: Customer Management only visible to MSP tenant admins (not SME tenant admins)
    if (link.key === 'customerManagement' && link.roles?.includes('tenant_admin')) {
        const isTenantAdmin = mainStore.hasRole(['tenant_admin'])
        if (isTenantAdmin) {
            const orgType = mainStore.organization?.type
            // Only show to MSP tenant admins, not SME tenant admins
            return orgType === 'msp'
        }
    }

    return true
}

/**
 * Translate with default: if key missing, use provided English label.
 * (You can drop te() and just do t(key, def) if you prefer.)
 * @param {string} key
 * @param {string} def
 * @returns {string}
 */
const tr = (key, def) => (te(key) ? t(key) : t(key, def))

const visibleMenus = computed(() =>
    (config.value.menus || [])
        .map((menu) => {
            const visibleLinks = (menu.links || []).filter((l) => isLinkVisible(l))
            if (visibleLinks.length === 0) return null

            return {
                ...menu,
                title: menu.key ? tr(`sidebar.${menu.key}`, menu.title) : menu.title,
                links: visibleLinks.map((link) => ({
                    ...link,
                    name: link.key ? tr(`sidebar.${link.key}`, link.name) : link.name
                }))
            }
        })
        .filter(Boolean)
)
</script>
